import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore, getFacilityKeyForPortal } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import { useTaskStore } from "@/store/task";
import { dobToDateInputValue } from "@/utils/intakeHelpers";
import { navigateToFirstFormTask } from "@/utils/navigateToFormHandler";
import { useIntakeForm } from "@/hooks/useIntakeForm";
import taskRequest from "@/api/task-request";
import { Button } from "@/components/ui/Button";
import { IntakePage } from "./IntakePage";
import { IntakeNav } from "./IntakeNav";

type IntakeRendererProps = {
  onExitFromFirstPage?: () => void;
};

export function IntakeRenderer({ onExitFromFirstPage }: IntakeRendererProps) {
  const navigate = useNavigate();
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id ?? 0);
  const patient = useProfileStore((s) => s.currentPatient);
  const demographics = useDemographicsStore((s) => s.demographics);

  const patientKey =
    patient?.patientKey != null && String(patient.patientKey).trim()
      ? String(patient.patientKey)
      : patient?.patientID != null
        ? String(patient.patientID)
        : "";

  const initialSeed = useMemo(() => {
    const first =
      demographics?.firstName?.trim() || patient?.firstName?.trim() || "";
    const last =
      demographics?.lastName?.trim() || patient?.lastName?.trim() || "";
    const childName = [first, last].filter(Boolean).join(" ");
    const dobRaw = demographics?.dob ?? patient?.dob ?? "";
    const childDob = dobToDateInputValue(
      typeof dobRaw === "string" ? dobRaw : String(dobRaw)
    );
    const guardianPhone =
      (demographics?.mobilePhone ?? "").trim() ||
      (demographics?.homePhone ?? "").trim() ||
      "";
    return {
      child_full_name: childName,
      child_dob: childDob,
      guardian_phone: guardianPhone,
      guardian_email: demographics?.email ?? "",
    };
  }, [
    demographics?.firstName,
    demographics?.lastName,
    demographics?.dob,
    demographics?.mobilePhone,
    demographics?.homePhone,
    demographics?.email,
    patient?.firstName,
    patient?.lastName,
    patient?.dob,
  ]);

  const form = useIntakeForm(caseHistoryId, patientKey, initialSeed);

  const {
    schemaMeta,
    visiblePages,
    currentPageIndex,
    currentPage,
    values,
    setFieldValue,
    ageDays,
    errors,
    isSubmitting,
    setIsSubmitting,
    submissionComplete,
    setSubmissionComplete,
    submissionError,
    setSubmissionError,
    goNext,
    goPrev,
    isLastPage,
    validateAllVisible,
    draftPrompt,
    continueDraft,
    discardDraft,
    clearDraftPersisted,
    serializeValue,
  } = form;

  const handleNext = () => {
    goNext();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    goPrev();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validateAllVisible()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const facilityKey = getFacilityKeyForPortal();
    if (!facilityKey || !caseHistoryId) {
      setSubmissionError("Missing facility or case history. Please try again.");
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      const problemsRes = await taskRequest.request.getCaseHistoryProblems(
        facilityKey,
        caseHistoryId
      );
      const problems = (problemsRes.data as { id?: number }[]) ?? [];
      const ordinal = problems.length + 1;
      const complaintType = String(values.primary_concern ?? "").trim();
      const name = String(values.child_full_name ?? "").trim();

      const createRes = await taskRequest.request.insertCaseHistoryProblem(
        facilityKey,
        caseHistoryId,
        ordinal,
        {
          complaintType,
          region: "",
          name: name || complaintType || "Pediatric intake",
        }
      );
      const raw = createRes.data as { id?: number } | number | undefined;
      const problemId =
        typeof raw === "number" ? raw : raw?.id;
      if (problemId == null || Number.isNaN(Number(problemId))) {
        throw new Error("Could not create case history problem.");
      }

      const entries = Object.entries(values)
        .map(([fieldId, rawValue]) => ({
          code: fieldId,
          value: serializeValue(rawValue),
        }))
        .filter((e) => e.value !== null) as { code: string; value: string }[];

      for (const entry of entries) {
        await taskRequest.request.updateCaseHistoryProblemValue(
          facilityKey,
          Number(problemId),
          { code: entry.code, value: entry.value }
        );
      }

      clearDraftPersisted();
      setSubmissionComplete(true);
    } catch (e) {
      console.error("Pediatric intake submit:", e);
      setSubmissionError(
        "Something went wrong submitting your form. Your progress has been saved — please try again or contact the front desk."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteNavigate = async () => {
    await navigateToFirstFormTask(navigate);
  };

  if (submissionComplete) {
    const gName = String(values.guardian_name ?? "").split(/\s+/)[0] || "there";
    const child = String(values.child_full_name ?? "");
    return (
      <div className="wizard-page__content px-4 pt-4 pb-8">
        <div className="complaint-history-card mx-auto max-w-lg p-6 text-center">
          <h2 className="text-xl font-bold text-slate-900">Thank you, {gName}</h2>
          {child && (
            <p className="mt-2 text-sm text-slate-700">
              We received the health history for <strong>{child}</strong>.
            </p>
          )}
          <p className="mt-3 text-sm text-slate-600">
            Your answers have been submitted. The doctor will review them before your visit.
          </p>
          <Button type="button" className="mt-6" onClick={handleCompleteNavigate}>
            Continue to forms
          </Button>
        </div>
      </div>
    );
  }

  const meta = schemaMeta as { title?: string; subtitle?: string };

  return (
    <div className="wizard-page__content px-4 pt-4 pb-8">
      {draftPrompt === "ask" && (
        <div
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950"
          role="status"
        >
          <p className="mb-3">We saved your progress. Continue where you left off?</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={continueDraft}>
              Continue
            </Button>
            <Button type="button" variant="outline" onClick={discardDraft}>
              Start over
            </Button>
          </div>
        </div>
      )}

      <div className="complaint-history-card p-4 sm:p-6">
        <p className="text-center text-sm text-slate-600">{meta.subtitle}</p>
        <h1 className="mt-1 text-center text-xl font-bold text-slate-900">
          {meta.title}
        </h1>

        {currentPage && draftPrompt === "resolved" && (
          <>
            <IntakePage
              page={currentPage}
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              ageDays={ageDays}
            />
            <IntakeNav
              currentIndex={currentPageIndex}
              totalPages={visiblePages.length}
              isLastPage={isLastPage}
              isSubmitting={isSubmitting}
              onBack={handleBack}
              onNext={handleNext}
              onSubmit={handleSubmit}
              onExitFromFirstPage={onExitFromFirstPage}
            />
          </>
        )}

        {draftPrompt === "ask" && (
          <p className="mt-4 text-center text-xs text-slate-500">
            Choose Continue or Start over to access the form.
          </p>
        )}
      </div>

      {submissionError && (
        <p className="mt-4 text-center text-sm text-destructive">{submissionError}</p>
      )}
    </div>
  );
}
